// Minimal component runtime: state + view(state) -> html, delegated events,
// dialog focus management (role=dialog + focus trap + Esc + focus return).
//
// Event wiring in templates:
//   <button data-click="addCard" data-id="123">   -> actions.addCard(el, event)
//   <input data-input="onQuery"> / data-change / data-keydown / data-submit
//   data-focus / data-blur (focusin / focusout)
// Dialogs: <div role="dialog" aria-modal="true" data-mcc-dialog> ... <button data-dialog-close data-click="close">
//   Esc clicks the topmost dialog's [data-dialog-close]. Tab is trapped in the topmost dialog.

import { morph } from './singles-hub--core--html.js?v=c7c57de095';

const EVENTS = [['click', 'click'], ['input', 'input'], ['change', 'change'], ['keydown', 'keydown'], ['submit', 'submit'], ['focusin', 'focus'], ['focusout', 'blur']];

export function mount(root, def) {
  const comp = {
    root,
    state: Object.assign({}, def.state || {}),
    actions: def.actions || {},
    _queued: false,
    _dialogs: new Set(),
    _returnFocus: null,
    setState(patch, cb) {
      const p = typeof patch === 'function' ? patch(comp.state) : patch;
      Object.assign(comp.state, p || {});
      comp.schedule(cb);
    },
    schedule(cb) {
      if (cb) (comp._cbs = comp._cbs || []).push(cb);
      if (comp._queued) return;
      comp._queued = true;
      Promise.resolve().then(() => { comp._queued = false; comp.render(); const cbs = comp._cbs || []; comp._cbs = []; cbs.forEach((f) => f()); });
    },
    render() {
      try {
        morph(root, def.view(comp.state, comp));
      } catch (err) {
        console.error('[mcc] render failed', err);
        throw err;
      }
      comp._afterRender();
      if (def.afterRender) def.afterRender(comp);
    },
    _afterRender() {
      const now = new Set(Array.from(root.querySelectorAll('[data-mcc-dialog]')).map((d) => d.id || d));
      const opened = [...now].filter((d) => !comp._dialogs.has(d));
      const closedAll = comp._dialogs.size > 0 && now.size === 0;
      if (opened.length) {
        if (!comp._dialogs.size) comp._returnFocus = document.activeElement;
        const ds = root.querySelectorAll('[data-mcc-dialog]');
        const top = ds[ds.length - 1];
        if (top && !top.contains(document.activeElement)) {
          const f = top.querySelector('[data-autofocus]') || top.querySelector('textarea, input:not([type=checkbox]):not([type=hidden]), select') || top.querySelector('button, [href]');
          if (f) setTimeout(() => { try { f.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }, 0);
        }
      }
      if (closedAll && comp._returnFocus && document.contains(comp._returnFocus)) {
        try { comp._returnFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        comp._returnFocus = null;
      }
      comp._dialogs = now;
      document.documentElement.classList.toggle('mcc-scroll-lock', !!document.querySelector('[data-mcc-dialog][data-lock]'));
    },
    destroy() {
      for (const [ev, , fn] of comp._handlers) root.removeEventListener(ev, fn, ev === 'focusin' || ev === 'focusout');
      root.innerHTML = '';
      if (def.onDestroy) def.onDestroy(comp);
    },
  };
  comp._handlers = EVENTS.map(([ev, attrName]) => {
    const fn = (e) => {
      let el = e.target;
      const key = 'data-' + attrName;
      while (el && el !== root.parentNode) {
        if (el.nodeType === 1 && el.hasAttribute(key)) {
          const name = el.getAttribute(key);
          const act = comp.actions[name];
          if (act) {
            if (ev === 'submit') e.preventDefault();
            if (ev === 'click' && el.tagName === 'A' && el.hasAttribute('data-prevent')) e.preventDefault();
            act(el, e, comp);
          } else console.warn('[mcc] no action', name);
          if (ev !== 'focusin' && ev !== 'focusout') break;
          return;
        }
        el = el.parentNode;
      }
    };
    root.addEventListener(ev, fn, ev === 'focusin' || ev === 'focusout');
    return [ev, attrName, fn];
  });
  comp.render();
  if (def.onMount) def.onMount(comp);
  return comp;
}

// ------------------------------------------------ global keyboard handling
let globalBound = false;
export function bindGlobalKeys(onEscNoDialog) {
  if (globalBound) return;
  globalBound = true;
  // outside click closes open popovers (filter dropdowns, ⋯ menus)
  document.addEventListener('mousedown', (e) => {
    document.querySelectorAll('[data-mcc-popover][data-open="true"]').forEach((pop) => {
      const owner = pop.closest('[data-popover-root]') || pop;
      if (owner.contains(e.target)) return;
      const c = pop.querySelector('[data-popover-close]') || owner.querySelector('[data-popover-close]');
      if (c) c.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });
  });
  document.addEventListener('keydown', (e) => {
    const ds = document.querySelectorAll('[data-mcc-dialog]');
    const top = ds[ds.length - 1];
    if (e.key === 'Escape') {
      // popovers (menus, filter dropdowns) close first
      const pop = document.querySelector('[data-mcc-popover][data-open="true"]');
      if (pop) {
        const c = pop.querySelector('[data-popover-close]') || pop;
        c.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        return;
      }
      if (top) {
        const c = top.querySelector('[data-dialog-close]');
        if (c) { e.preventDefault(); c.click(); }
        return;
      }
      if (onEscNoDialog) onEscNoDialog(e);
      return;
    }
    if (e.key !== 'Tab' || !top) return;
    const f = Array.from(top.querySelectorAll('button:not([disabled]), [href], input:not([disabled]):not([type=hidden]), select, textarea, [tabindex]:not([tabindex="-1"])')).filter((x) => x.offsetParent !== null || x === document.activeElement);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1], act = document.activeElement;
    if (!top.contains(act)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && act === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && act === last) { e.preventDefault(); first.focus(); }
  });
}
